/**
 * The questions the last reply asked, docked to the top of the composer until
 * they are answered, one at a time.
 *
 * The options are numbered rows and a number key picks one. A single pick
 * moves on at once; where several may be picked they are checkboxes and a
 * Next. The last question's answer sends them all together, and Back goes to
 * the one before. "Something else" is the last row: with one question it
 * moves to the composer's text box, and anything sent while the question
 * waits is its answer, even after Esc has hidden the dock; with several it
 * opens a line for that question's own answer.
 *
 * It never takes the focus by itself: a message that starts with a digit
 * would pick an option. Reached by Tab or a click, a number picks, and any
 * other character moves the focus to the text box, taking the character.
 */

import React, { useId, useState, type CSSProperties, type KeyboardEvent } from 'react';
import type { Ask } from '../api';
import { ds } from '../ds';
import { joinPicks, optionForKey } from './pendingAsk';
import { IconTap, Ph } from './ui';

const row: CSSProperties = {
  width: '100%',
  minHeight: 38,
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '7px 10px',
  borderRadius: 6,
  fontSize: 13.5,
  lineHeight: 1.4,
  color: 'var(--text-strong)'
};

const lead: CSSProperties = {
  width: 12,
  flex: 'none',
  fontFamily: 'var(--font-mono)',
  fontSize: 10.5,
  color: 'var(--text-faint)'
};

export const QuestionDock = React.forwardRef<
  HTMLDivElement,
  {
    ask: Ask;
    /** One answer per question, in order. */
    onAnswer: (picks: string[]) => void;
    /** "Something else" on a lone question, or a character typed here: over
     *  to the text box. */
    onWrite: () => void;
    /** Esc or ×: hidden for these questions. */
    onHide: () => void;
  }
>(function QuestionDock({ ask, onAnswer, onWrite, onHide }, ref) {
  const qid = useId();
  const count = ask.questions.length;
  const [step, setStep] = useState(0);
  const [picks, setPicks] = useState<string[]>(() => ask.questions.map(() => ''));
  const [ticked, setTicked] = useState<ReadonlyArray<ReadonlySet<number>>>(() => ask.questions.map(() => new Set()));
  // Several questions: the line for this question's own answer, when open.
  const [writing, setWriting] = useState(false);
  const [other, setOther] = useState('');
  // Single pick: the option Tab lands on, the others reached by arrow.
  const [active, setActive] = useState(0);

  const q = ask.questions[step]!;
  const multi = q.multiSelect;
  const picked = ticked[step]!;
  const lastStep = step === count - 1;

  const go = (to: number) => {
    setStep(to);
    setWriting(false);
    setOther('');
    setActive(0);
  };

  /** This question's answer: send them all after the last, else move on. */
  const choose = (answer: string) => {
    const next = picks.map((p, i) => (i === step ? answer : p));
    setPicks(next);
    if (lastStep) onAnswer(next);
    else go(step + 1);
  };

  const toggle = (i: number) =>
    setTicked((all) =>
      all.map((set, at) => {
        if (at !== step) return set;
        const next = new Set(set);
        if (!next.delete(i)) next.add(i);
        return next;
      })
    );

  const sendTicked = () => {
    if (picked.size) choose(joinPicks(q.options, picked));
  };

  const somethingElse = () => {
    if (count === 1) onWrite();
    else setWriting(true);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.nativeEvent.isComposing || e.ctrlKey || e.metaKey || e.altKey) return;
    const target = e.target as HTMLElement;
    if (target.tagName === 'INPUT') {
      if (e.key === 'Escape') {
        e.preventDefault();
        setWriting(false);
      }
      return;
    }
    const n = optionForKey(e.key, q.options.length);
    if (n !== null) {
      e.preventDefault();
      if (multi) toggle(n);
      else choose(q.options[n]!);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onHide();
    } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const rows = [...e.currentTarget.querySelectorAll<HTMLElement>('[data-row]')];
      const at = rows.indexOf(target);
      const by = e.key === 'ArrowDown' ? 1 : -1;
      rows[at < 0 ? (by > 0 ? 0 : rows.length - 1) : (at + by + rows.length) % rows.length]?.focus();
    } else if (e.key === 'Enter' && multi && target.getAttribute('role') === 'checkbox') {
      // Space ticks a box; Enter moves on with what is ticked.
      e.preventDefault();
      sendTicked();
    } else if (e.key.length === 1 && e.key !== ' ' && count === 1) {
      // Not prevented: the character lands where the focus has gone.
      onWrite();
    }
  };

  const { Button } = ds();
  const nextLabel = lastStep ? 'Send' : 'Next';

  return (
    <div
      ref={ref}
      role="group"
      aria-labelledby={qid}
      tabIndex={-1}
      onKeyDown={onKeyDown}
      className="fade-in scroll"
      style={{
        margin: '0 10px',
        padding: '8px 6px 6px',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        maxHeight: 'min(360px, 45vh)',
        overflowY: 'auto',
        background: 'var(--surface-card)',
        border: '1px solid var(--border-subtle)',
        borderBottom: 0,
        borderRadius: 'var(--radius-lg) var(--radius-lg) 0 0',
        outline: 'none'
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '0 0 4px 10px' }}>
        <span
          id={qid}
          style={{
            flex: 1,
            minWidth: 0,
            paddingTop: 5,
            fontSize: 14,
            fontWeight: 600,
            lineHeight: 1.45,
            color: 'var(--text-strong)',
            overflowWrap: 'break-word'
          }}
        >
          {q.question}
        </span>
        {count > 1 ? (
          <span style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: 2, paddingTop: 2 }}>
            {step > 0 ? <IconTap icon="caret-left" label="Back" size={24} glyph={13} className="tap" onClick={() => go(step - 1)} /> : null}
            <span
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 11,
                color: 'var(--text-faint)',
                padding: '0 4px',
                whiteSpace: 'nowrap'
              }}
            >
              {step + 1} of {count}
            </span>
          </span>
        ) : null}
        <IconTap icon="x" label="Close" size={28} glyph={14} className="tap" onClick={onHide} />
      </div>

      <div
        key={step}
        role={multi ? undefined : 'listbox'}
        aria-labelledby={multi ? undefined : qid}
        style={{ display: 'flex', flexDirection: 'column', gap: 2 }}
      >
        {q.options.map((o, i) => {
          const on = multi ? picked.has(i) : picks[step] === o;
          return (
            <button
              key={i}
              type="button"
              data-row
              role={multi ? 'checkbox' : 'option'}
              aria-checked={multi ? on : undefined}
              aria-selected={multi ? undefined : on}
              tabIndex={multi || i === active ? 0 : -1}
              onFocus={multi ? undefined : () => setActive(i)}
              onClick={() => (multi ? toggle(i) : choose(o))}
              className="bare hov-row"
              style={row}
            >
              <span style={lead}>{i + 1}</span>
              {multi ? (
                <Ph
                  name={on ? 'check-square' : 'square'}
                  fill={on}
                  size={16}
                  color={on ? 'var(--primary)' : 'var(--text-faint)'}
                />
              ) : null}
              <span style={{ flex: 1, minWidth: 0, overflowWrap: 'break-word' }}>{o}</span>
              {!multi && on ? <Ph name="check" size={14} color="var(--primary)" /> : null}
            </button>
          );
        })}
      </div>

      {writing ? (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (other.trim()) choose(other.trim());
          }}
          style={{ ...row, gap: 8 }}
        >
          <Ph name="pencil-simple" size={12} color="var(--text-faint)" style={{ width: 12, flex: 'none' }} />
          <input
            autoFocus
            value={other}
            onChange={(e) => setOther(e.target.value)}
            maxLength={500}
            placeholder="Your answer"
            aria-label={`Your answer to: ${q.question}`}
            style={{ flex: 1, minWidth: 0, border: 0, background: 'transparent', color: 'var(--text-strong)', fontSize: 13.5 }}
          />
          <Button variant="primary" density="app" size="sm" type="submit" disabled={!other.trim()}>
            {nextLabel}
          </Button>
        </form>
      ) : (
        <button type="button" data-row onClick={somethingElse} className="bare hov-row" style={{ ...row, color: 'var(--text-muted)' }}>
          <Ph name="pencil-simple" size={12} color="var(--text-faint)" style={{ width: 12, flex: 'none' }} />
          Something else
        </button>
      )}

      {multi ? (
        <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '4px 4px 2px' }}>
          <Button variant="primary" density="app" size="sm" disabled={!picked.size} onClick={sendTicked}>
            {nextLabel}
          </Button>
        </div>
      ) : null}
    </div>
  );
});
